import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type LessonGrades = components["schemas"]["LessonGrades"];
export type StudentMarks = components["schemas"]["StudentMarks"];
export type StudentMark = components["schemas"]["StudentMark"];
export type AssessmentKind = components["schemas"]["AssessmentKind"];
export type AssessmentTypeDetails = components["schemas"]["AssessmentTypeDetails"];
export type ClassLessonGrades = components["schemas"]["ClassLessonGrades"];
export type MarkEditDetails = components["schemas"]["MarkEditDetails"];
export type ClosedMarkingPeriod = components["schemas"]["ClosedMarkingPeriodDetails"];

/** Mirrors LessonContent.TopicMaxLength and HomeworkMaxLength on the server. */
export const TOPIC_MAX_LENGTH = 300;
export const HOMEWORK_MAX_LENGTH = 2000;

/** Mirrors AssessmentType.MinScore, MaxScoreLimit and NameMaxLength on the server. */
export const MIN_SCORE = 1;
export const MAX_SCORE_LIMIT = 100;
export const ASSESSMENT_NAME_MAX_LENGTH = 100;

export const gradingKeys = {
  all: ["grading"] as const,
  classWeek: (classId: string, from: string) => ["grading", "class", classId, from] as const,
  lesson: (lessonId: string) => ["grading", "lesson", lessonId] as const,
  edits: (lessonId: string) => ["grading", "lesson", lessonId, "edits"] as const,
  types: ["grading", "types"] as const,
  closed: (page: number) => ["grading", "closed", page] as const,
};

/** The class's lessons from `from` to `to` that the caller may open (GET /grading/classes/{classId}), keyed by lesson id. */
export function useClassGrades(classId: string, from: string, to: string, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: gradingKeys.classWeek(classId, from),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/classes/{classId}", { params: { path: { classId }, query: { From: from, To: to } } });
      if (error || !data) throw new Error(t("app.error"));
      return new Map(data.map((lesson) => [lesson.lessonId, lesson]));
    },
    enabled,
  });
}

export function useAssessmentTypes() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: gradingKeys.types,
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/assessment-types");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

export const studentFullName = (s: { lastName: string; firstName: string; middleName?: string | null }) =>
  [s.lastName, s.firstName, s.middleName].filter(Boolean).join(" ");
